import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import config from '../config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { logActivity } from '../helpers/logActivity.helper';
import { toast } from 'react-toastify';

// PARENTS
export default function useParents() {
  const [searchString, setSearchString] = useState('');
  const queryClient = useQueryClient();

  // API CALL - GET PARENTS
  const { data, isLoading, refetch } = useQuery(['parents'], async () => {
    const parentCall = new FormData();
    parentCall.append('auth', localStorage.getItem('token'));
    if (document.getElementById('parentSearchField').value !== '') {
      parentCall.append(
        'parentSearch',
        document.getElementById('parentSearchField').value
      );
    }
    const output = await axios.post(config.PARENTS_API, parentCall);
    if (output.data.response.status === 'OK')
      return output.data.response.parents;
  });

  const checkSearchOnKeyDown = e => {
    if (e.key === 'Enter') {
      queryClient.invalidateQueries({ queryKey: ['parents'] });
    }
  };

  return {
    data,
    isLoading,
    searchString,
    setSearchString,
    checkSearchOnKeyDown,
  };
}

// PARENTDETAILS
export function useParentDetails() {
  const { parent_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [parentDetails, setParentDetails] = useState([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackBarType, setSnackBarType] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setParentDetails(data.parent);
    }
  }, []);

  // API CALL - GET PARENT DETAILS
  const { data, refetch } = useQuery(['parentDetail', parent_id], async () => {
    const parentData = new FormData();
    parentData.append('mode', 'getParentById');
    parentData.append('auth', localStorage.getItem('token'));
    parentData.append('parentId', parent_id);
    const output = await axios.post(config.PARENTS_API, parentData);
    if (output.data.response.status === 'OK') {
      setParentDetails(() => {
        return {
          ...output.data.response.parent,
          mode: 'updateParentWithId',
          auth: localStorage.getItem('token'),
        };
      });
      return output.data.response;
    }
  });

  // MUTATION - SAVE DETAILS
  const mutation = useMutation(async () => {
    const output = await axios.post(config.PARENTS_API, parentDetails);
    if (output.data.response.status === 'OK') {
      setSnackBarType('success');
      setSnackMsg('Parent updates saved');
      setSnackOpen(true);
    }
  });
  const buttonLoadingStatus = mutation.isLoading;

  const hasChildren = data?.children?.length > 0;

  const openDeleteDialog = () => setDeleteDialogOpen(true);
  const closeDeleteDialog = () => setDeleteDialogOpen(false);

  const deleteMutation = useMutation(async () => {
    const deleteData = new FormData();
    deleteData.append('mode', 'deleteParent');
    deleteData.append('parentId', parent_id);
    deleteData.append('auth', localStorage.getItem('token'));
    const output = await axios.post(config.PARENTS_API, deleteData);
    const status = output.data.response.status;
    if (status === 'OK') {
      logActivity(
        `Deleted parent ${data?.parent?.parent_name ?? ''} (${parent_id})`
      );
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['parents'] });
      toast.success(
        `Parent ${data?.parent?.parent_name ?? ''} deleted successfully`,
        { position: toast.POSITION.BOTTOM_LEFT }
      );
      navigate('/manage/parents');
    } else if (status === 'HAS-CHILDREN') {
      setSnackBarType('error');
      setSnackMsg('Cannot delete: parent still has attached children.');
      setSnackOpen(true);
      setDeleteDialogOpen(false);
    } else {
      setSnackBarType('error');
      setSnackMsg('Delete failed. Please try again.');
      setSnackOpen(true);
      setDeleteDialogOpen(false);
    }
  });

  const submitDeleteParent = () => {
    deleteMutation.mutate();
  };

  const handleFormUpdate = e => {
    setParentDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };
  const formSubmission = () => {
    mutation.mutate();
  };
  const snackClose = () => {
    setSnackOpen(false);
  };

  return {
    data,
    refetch,
    parentDetails,
    handleFormUpdate,
    formSubmission,
    buttonLoadingStatus,
    snackOpen,
    snackClose,
    snackMsg,
    snackBarType,
    hasChildren,
    deleteDialogOpen,
    openDeleteDialog,
    closeDeleteDialog,
    submitDeleteParent,
    deleteLoading: deleteMutation.isLoading,
  };
}
